// Generic audit-history viewer. Shows recent edits for a specific
// record (or for an entire table when no recordId is given).
//
// Usage:
//   <AuditHistory table="clients" recordId={household.id} title="Activity" />
//   <AuditHistory table="firm_settings" />

import { useEffect, useState } from 'react';
import { fetchAuditLog } from '../../utils/insuranceApi';

const ACTION_COLORS = {
  insert: 'bg-deep-teal/15 text-deep-teal',
  update: 'bg-bullogic-blue/15 text-bullogic-blue',
  delete: 'bg-rose/15 text-rose',
  bulk_upsert: 'bg-amber-100 text-amber-700',
};

function fmtTime(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch {
    return ts;
  }
}

export default function AuditHistory({ table, recordId, title, limit = 25 }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchAuditLog({ table, recordId, limit })
      .then(list => { if (!cancelled) { setEntries(list); setLoading(false); } })
      .catch(err => { if (!cancelled) { setError(err.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [table, recordId, limit]);

  if (loading) return <div className="text-[11px] text-[#888]">Loading activity...</div>;
  if (error) return <div className="text-[11px] text-rose">Failed to load activity: {error}</div>;
  if (entries.length === 0) {
    return <div className="text-[11px] text-[#888] italic">No activity recorded yet.</div>;
  }

  return (
    <div className="space-y-1.5">
      {title && (
        <div className="text-[10px] font-bold uppercase tracking-wider text-[#888]">{title}</div>
      )}
      {entries.map(e => {
        const isExpanded = expandedId === e.id;
        const colors = ACTION_COLORS[e.action] || 'bg-gray-100 text-gray-600';
        return (
          <div key={e.id} className="bg-bone/30 rounded-[3px] px-3 py-1.5 text-[11px]">
            <div
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => setExpandedId(isExpanded ? null : e.id)}
            >
              <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-[2px] ${colors}`}>
                {e.action}
              </span>
              <span className="text-navy">{e.summary || `${e.action} on ${e.table_name}`}</span>
              <span className="text-[#888] flex-1 text-right">{fmtTime(e.changed_at)}</span>
              <span className="text-[#888]">{e.changed_by || 'unknown'}</span>
              <span className="text-[10px] text-[#aaa]">{isExpanded ? '▼' : '▶'}</span>
            </div>
            {isExpanded && (
              <div className="mt-2 grid grid-cols-2 gap-3 text-[10px]">
                <div>
                  <div className="font-bold text-[#666] mb-1">Before</div>
                  <pre className="bg-white p-2 rounded-[3px] border border-gray-200 overflow-auto max-h-40 text-[10px] leading-tight">
                    {e.before ? JSON.stringify(e.before, null, 2) : '(none)'}
                  </pre>
                </div>
                <div>
                  <div className="font-bold text-[#666] mb-1">After</div>
                  <pre className="bg-white p-2 rounded-[3px] border border-gray-200 overflow-auto max-h-40 text-[10px] leading-tight">
                    {e.after ? JSON.stringify(e.after, null, 2) : '(none)'}
                  </pre>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
